import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BlockTypeEnum } from '@core/enums';
import {
  FilterRangeService
} from '@modules/creator/components/special-block/advanced-filters/components/filter-range/filter-range.service';
import { getDefaultAllFiltersValue, getDefaultSearchValue } from '@shared/services/filters/defaults';
import {
  ActiveFilterData,
  ActiveFilters,
  AvailableFilters,
  FilterTypeRanges,
  SearchValue,
  SetFiltersParams,
  UpdateAvailableFilters,
  UpdateAvailableFiltersReason,
} from '@shared/services/filters/types';
import { SessionStorageService } from '@shared/services/storage';
import { camelCase, snakeCase } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

interface AdvancedFiltersSnapshot {
  pathname: string;
  filters: AvailableFilters;
  searchTerm: string;
  extendedSearch: boolean;
  rangesStart: Record<string, number | null>;
  rangesEnd: Record<string, number | null>;
  allCount: number | null;
}

@Injectable({providedIn: 'root'})
export class AdvancedFiltersService {
  private static readonly SESSION_STORAGE_KEY = 'advancedFiltersState';
  private static _allCount: number | null = null;
  private static savedAvailableFilters: BehaviorSubject<AvailableFilters> = new BehaviorSubject<AvailableFilters>({} as AvailableFilters);
  private static availableFilters: BehaviorSubject<AvailableFilters> = new BehaviorSubject<AvailableFilters>({} as AvailableFilters);
  private static availableFiltersUpdateSubject: BehaviorSubject<UpdateAvailableFilters> = new BehaviorSubject<UpdateAvailableFilters>(getDefaultAllFiltersValue());
  private static paramsForList: Subject<ActiveFilters> = new Subject<ActiveFilters>();
  private static search: SearchValue = getDefaultSearchValue();
  private static savedSearch: SearchValue = getDefaultSearchValue();
  private static initialListParams: ActiveFilters = {} as ActiveFilters;
  private static activeFiltersList: BehaviorSubject<ActiveFilterData[]> = new BehaviorSubject<ActiveFilterData[]>([] as ActiveFilterData[]);

  constructor(private filterRangeService: FilterRangeService,
              private sessionStorageService: SessionStorageService,
              @Inject(DOCUMENT) private document: Document) {
  }

  get paramsForListUpdated$(): Observable<ActiveFilters> {
    return AdvancedFiltersService.paramsForList.asObservable();
  }

  get activeFiltersListUpdated$(): Observable<ActiveFilterData[]> {
    return AdvancedFiltersService.activeFiltersList.asObservable();
  }

  get availableFiltersUpdated$(): Observable<UpdateAvailableFilters> {
    return AdvancedFiltersService.availableFiltersUpdateSubject.asObservable();
  }

  get allFiltersValue(): UpdateAvailableFilters {
    return AdvancedFiltersService.availableFiltersUpdateSubject.getValue();
  }

  removeFilter(type: string, id: number): void {
    let {searchTerm, extendedSearch} = this.getSearchValue();
    const allParams = AdvancedFiltersService.availableFilters.getValue();
    if (type === 'searchTerm') {
      searchTerm = '';
      extendedSearch = false;
    } else if (type === FilterTypeRanges.PRICE) {
      this.resetRangeToBounds(BlockTypeEnum.RANGE_PRICE_OF_CREATION, FilterTypeRanges.PRICE);
    } else if (type === FilterTypeRanges.YEAR) {
      this.resetRangeToBounds(BlockTypeEnum.RANGE_YEAR_OF_CREATION, FilterTypeRanges.YEAR);
    } else {
      allParams[type] = allParams[type].map(filter => {
        if (filter.id === id) {
          filter.active = false;
        }
        return filter;
      });
    }
    this.setFilters({filters: allParams, searchTerm, extendedSearch}, UpdateAvailableFiltersReason.userAction);
    this.saveAvailableFilters();
    this.updateParamsForList();
  }

  private updateActiveFiltersList(): void {
    const allParams = AdvancedFiltersService.availableFilters.getValue();
    const {searchTerm} = this.getSearchValue();
    const activeFilters: ActiveFilterData[] = [];

    Object.keys(allParams).forEach(filterType => {
      const values = allParams[filterType];
      if (!Array.isArray(values) || values.length === 0) {
        return;
      }
      activeFilters.push(...values.filter(filter => filter.active).map(filter => ({...filter, type: filterType})));
    });

    if (searchTerm) {
      activeFilters.push({
        id: -1,
        name: searchTerm,
        type: 'searchTerm',
        display: true,
        active: true,
      });
    }

    const priceChip = this.buildActiveRangeChip(
      BlockTypeEnum.RANGE_PRICE_OF_CREATION,
      FilterTypeRanges.PRICE,
      allParams.priceRanges,
    );
    if (priceChip) {
      activeFilters.push(priceChip);
    }

    const yearChip = this.buildActiveRangeChip(
      BlockTypeEnum.RANGE_YEAR_OF_CREATION,
      FilterTypeRanges.YEAR,
      allParams.yearRanges,
    );
    if (yearChip) {
      activeFilters.push(yearChip);
    }

    AdvancedFiltersService.activeFiltersList.next(activeFilters);
  }

  /**
   * Chip zakresu tylko gdy użytkownik zawęził min/max względem pełnego
   * zakresu — inaczej pasek chipów wyglądałby jak aktywny filtr „na wszystko”.
   */
  private buildActiveRangeChip(
    blockType: BlockTypeEnum,
    type: FilterTypeRanges,
    bounds: {min?: number; max?: number} | undefined,
  ): ActiveFilterData | null {
    const range = this.getEffectiveRange(blockType, type);
    if (range.start == null && range.end == null) {
      return null;
    }

    const min = bounds?.min ?? this.filterRangeService.minValueStart[blockType];
    const max = bounds?.max ?? this.filterRangeService.maxValueEnd[blockType];
    const labelStart = range.start ?? min ?? '';
    const labelEnd = range.end ?? max ?? '';

    return {
      id: blockType,
      name: `${labelStart} – ${labelEnd}`,
      type,
      display: true,
      active: true,
    };
  }

  private resetRangeToBounds(blockType: BlockTypeEnum, rangeKey: FilterTypeRanges): void {
    const bounds = AdvancedFiltersService.availableFilters.getValue()[rangeKey];
    const min = bounds?.min ?? this.filterRangeService.minValueStart[blockType] ?? null;
    const max = bounds?.max ?? this.filterRangeService.maxValueEnd[blockType] ?? null;
    this.filterRangeService.filterStart[blockType] = min;
    this.filterRangeService.filterEnd[blockType] = max;
  }

  private isEmptyRangeValue(value: unknown): boolean {
    return value === null
      || value === undefined
      || value === ''
      || (typeof value === 'number' && Number.isNaN(value));
  }

  /**
   * Aktywny zakres: null = brak strony; jednostronny (tylko min lub tylko max) jest OK.
   * Pełny min–max obu stron = brak filtra.
   */
  private getEffectiveRange(blockType: BlockTypeEnum, rangeKey: FilterTypeRanges): {start: number | null, end: number | null} {
    const {filterStart, filterEnd, minValueStart, maxValueEnd} = this.filterRangeService;
    const rawStart = filterStart[blockType];
    const rawEnd = filterEnd[blockType];

    const startEmpty = this.isEmptyRangeValue(rawStart);
    const endEmpty = this.isEmptyRangeValue(rawEnd);

    if (startEmpty && endEmpty) {
      return {start: null, end: null};
    }

    const bounds = AdvancedFiltersService.availableFilters.getValue()[rangeKey];
    const min = Number(bounds?.min ?? minValueStart[blockType]);
    const max = Number(bounds?.max ?? maxValueEnd[blockType]);
    const start = startEmpty ? null : Number(rawStart);
    const end = endEmpty ? null : Number(rawEnd);

    if (
      start !== null
      && end !== null
      && Number.isFinite(min)
      && Number.isFinite(max)
      && start === min
      && end === max
    ) {
      return {start: null, end: null};
    }

    return {
      start: start !== null && Number.isFinite(start) ? start : null,
      end: end !== null && Number.isFinite(end) ? end : null,
    };
  }

  private applyEffectiveRangeToFilters(
    activeFilters: ActiveFilters,
    blockType: BlockTypeEnum,
    rangeKey: FilterTypeRanges,
    options: {fromKey: 'priceFrom' | 'yearFrom', toKey: 'priceTo' | 'yearTo', endAliasKey?: 'priceEnd' | 'yearEnd'},
  ): void {
    const range = this.getEffectiveRange(blockType, rangeKey);
    if (range.start !== null) {
      if (options.fromKey === 'priceFrom') {
        activeFilters.priceFrom = range.start;
      } else {
        activeFilters.yearFrom = range.start;
      }
    }
    if (range.end !== null) {
      if (options.toKey === 'priceTo') {
        activeFilters.priceTo = range.end;
        if (options.endAliasKey === 'priceEnd') {
          activeFilters.priceEnd = range.end;
        }
      } else {
        activeFilters.yearTo = range.end;
        if (options.endAliasKey === 'yearEnd') {
          activeFilters.yearEnd = range.end;
        }
      }
    }
  }

  private getDataForQueryCount(filtersData?: AvailableFilters): AvailableFilters {
    const filters = filtersData ?? AdvancedFiltersService.availableFilters.getValue();
    const listParams = AdvancedFiltersService.initialListParams;
    const dataForQueryCount: AvailableFilters = {};
    Object.keys(filters).forEach(filterType => {
      if (filterType === 'priceRanges') {
        dataForQueryCount.priceFrom = filters[filterType].min;
        dataForQueryCount.priceTo = filters[filterType].max;
        return;
      }
      if (filterType === 'yearRanges') {
        dataForQueryCount.yearFrom = filters[filterType].min;
        dataForQueryCount.yearTo = filters[filterType].max;
        return;
      }
      if (filterType === 'priceRanges' || filterType === 'yearRanges') {
        dataForQueryCount[filterType] = filters[filterType];
        return;
      }
      let value = filters[filterType].filter(filter => filter.active);
      if (!value.length) {
        const valueList = listParams[filterType] || [];
        value = filters[filterType].filter(filter => valueList.includes(filter.id));
      }
      dataForQueryCount[filterType] = value;
    });
    return dataForQueryCount;
  }

  setInitialListParams(params: ActiveFilters): void {
    if (!params) {
      AdvancedFiltersService.initialListParams = {} as ActiveFilters;
      return;
    }
    const obj: ActiveFilters = {} as ActiveFilters;
    Object.keys(params).forEach(rawKey => {
      const key = camelCase(rawKey);
      const values = this.normalizeInitialListParam(params[rawKey]);

      if (!values.length) {
        return;
      }

      if (['priceFrom', 'priceTo', 'yearFrom', 'yearTo'].includes(key)) {
        obj[key] = values[0] as any;
        return;
      }

      if (key === 'availability') {
        const availability = values.filter(item => item !== 0);
        if (availability.length) {
          obj.availability = availability;
        }
        return;
      }

      if (!values.includes(-1)) {
        obj[key] = values;
      }
    });
    AdvancedFiltersService.initialListParams = obj;
  }

  getInitialListParamsValue(): ActiveFilters {
    return structuredClone(AdvancedFiltersService.initialListParams);
  }

  private normalizeInitialListParam(value: unknown): number[] {
    const values = Array.isArray(value) ? value : [value];

    return values
      .map(item => Number(item))
      .filter(item => Number.isFinite(item));
  }

  getUserFiltersValue(filtersData?: AvailableFilters): ActiveFilters {
    const filters = filtersData ?? AdvancedFiltersService.availableFilters.getValue();
    const activeFilters: ActiveFilters = {};

    Object.keys(filters).forEach(filterType => {
      if (filterType === 'priceRanges' || filterType === 'yearRanges') {
        return;
      }

      const values = filters[filterType];
      if (!Array.isArray(values)) {
        return;
      }

      const activeIds = values
        .filter(filter => filter.active)
        .map(filter => filter.id);

      if (activeIds.length) {
        activeFilters[filterType] = activeIds;
      }
    });

    this.applyEffectiveRangeToFilters(
      activeFilters,
      BlockTypeEnum.RANGE_PRICE_OF_CREATION,
      FilterTypeRanges.PRICE,
      {fromKey: 'priceFrom', toKey: 'priceTo'},
    );
    this.applyEffectiveRangeToFilters(
      activeFilters,
      BlockTypeEnum.RANGE_YEAR_OF_CREATION,
      FilterTypeRanges.YEAR,
      {fromKey: 'yearFrom', toKey: 'yearTo'},
    );

    return activeFilters;
  }

  /**
   * Zakresy ceny i roku żyją poza `availableFilters` — trzyma je
   * `FilterRangeService`. Ich zmiana nie przechodzi przez `setFilters`, więc
   * panel filtrów nigdy nie dostawał sygnału do przeliczenia liczników.
   * Emitujemy `userAction` bez modyfikowania stanu filtrów — `getFiltersCounts`
   * odczyta aktualne zakresy sam, przez `getUserFiltersValue()`.
   */
  notifyRangesChanged(): void {
    this.setFilters(
      {filters: this.getAvailableFiltersValue()},
      UpdateAvailableFiltersReason.userAction,
    );
  }

  setParamsForList(params: ActiveFilters): void {
    AdvancedFiltersService.paramsForList.next(params);
  }

  updateParamsForList(): void {
    this.setParamsForList(
      this.getActiveFiltersValue(),
    );
  }

  getSearchValue(): SearchValue {
    return {
      searchTerm: AdvancedFiltersService.search.searchTerm.trim(),
      extendedSearch: AdvancedFiltersService.search.extendedSearch,
    } as SearchValue;
  }

  initializeAvailableFilters(filters: AvailableFilters): void {
    filters = this.applyListFiltersToAvailableFilters(filters);
    AdvancedFiltersService.savedAvailableFilters.next(structuredClone(filters));
    this.updateActiveFiltersList();
    this.setFilters({
      filters: structuredClone(filters),
      searchTerm: '',
      extendedSearch: false,
    }, UpdateAvailableFiltersReason.initialization);
  }

  saveAvailableFilters(): void {
    const filters = this.getAvailableFiltersValue();
    AdvancedFiltersService.savedAvailableFilters.next(structuredClone(filters));
    AdvancedFiltersService.savedSearch = structuredClone(this.getSearchValue());
    this.persistStateInSession();
  }

  // Zatwierdzony stan filtrów trafia do sessionStorage, żeby przetrwał wejście
  // w podgląd pracy i powrót Wstecz oraz odświeżenie strony. sessionStorage
  // jest per-karta, więc otwarcie adresu w nowej karcie zawsze daje stan domyślny.
  private persistStateInSession(): void {
    const {filterStart, filterEnd} = this.filterRangeService;
    const snapshot: AdvancedFiltersSnapshot = {
      pathname: this.document.location.pathname,
      filters: AdvancedFiltersService.savedAvailableFilters.getValue(),
      searchTerm: AdvancedFiltersService.savedSearch.searchTerm,
      extendedSearch: AdvancedFiltersService.savedSearch.extendedSearch,
      rangesStart: filterStart,
      rangesEnd: filterEnd,
      allCount: AdvancedFiltersService._allCount,
    };
    this.sessionStorageService.setItem(AdvancedFiltersService.SESSION_STORAGE_KEY, JSON.stringify(snapshot));
  }

  // Odtwarza stan filtrów ze snapshotu, o ile dotyczy on strony o podanym
  // pathname — snapshot zapisany na innej liście nie może "przeciec" między
  // stronami. Zwraca informację, czy odtworzenie się odbyło.
  restoreStateFromSession(pathname: string): boolean {
    const raw = this.sessionStorageService.getItem(AdvancedFiltersService.SESSION_STORAGE_KEY);
    if (!raw) {
      return false;
    }

    let snapshot: AdvancedFiltersSnapshot;
    try {
      snapshot = JSON.parse(raw);
    } catch {
      this.clearPersistedState();
      return false;
    }

    if (!snapshot || snapshot.pathname !== pathname) {
      return false;
    }

    Object.assign(this.filterRangeService.filterStart, snapshot.rangesStart || {});
    Object.assign(this.filterRangeService.filterEnd, snapshot.rangesEnd || {});
    AdvancedFiltersService._allCount = snapshot.allCount ?? null;
    this.setFilters({
      filters: snapshot.filters || ({} as AvailableFilters),
      searchTerm: snapshot.searchTerm || '',
      extendedSearch: !!snapshot.extendedSearch,
    }, UpdateAvailableFiltersReason.systemUpdate);
    this.saveAvailableFilters();

    return true;
  }

  clearPersistedState(): void {
    this.sessionStorageService.removeItem(AdvancedFiltersService.SESSION_STORAGE_KEY);
  }

  restoreAvailableFilters(): void {
    const filters = AdvancedFiltersService.savedAvailableFilters.getValue();
    const {searchTerm, extendedSearch} = AdvancedFiltersService.savedSearch;
    this.setFilters({
      filters,
      searchTerm,
      extendedSearch,
    }, UpdateAvailableFiltersReason.systemUpdate);
  }

  private applyListFiltersToAvailableFilters(filters: AvailableFilters): AvailableFilters {
    const initialListParams = AdvancedFiltersService.initialListParams;
    Object.keys(filters).forEach(filterType => {
      const filterValues = filters[filterType];
      const initialForType = initialListParams[filterType] || [];
      if (filterValues.length > 0 && initialForType.length > 0) {
        filters[filterType].forEach(filter => {
          filter.display = initialForType.includes(filter.id);
        });
      }
    });
    return filters;
  }

  setFilters(params: SetFiltersParams, reason: UpdateAvailableFiltersReason = UpdateAvailableFiltersReason.systemUpdate): void {
    let {filters, searchTerm, extendedSearch} = params;
    if (filters === undefined && searchTerm === undefined && extendedSearch === undefined) {
      return;
    }

    if (filters === undefined) {
      filters = this.getAvailableFiltersValue();
    }

    const originalSearch = this.getSearchValue();
    if (searchTerm !== undefined) {
      searchTerm = (searchTerm || '').trim();
    } else {
      searchTerm = originalSearch.searchTerm;
    }

    if (extendedSearch === undefined) {
      extendedSearch = originalSearch.extendedSearch;
    }

    if (params._allCount !== undefined) {
      AdvancedFiltersService._allCount = params._allCount;
    }

    AdvancedFiltersService.search = {
      searchTerm,
      extendedSearch,
    };
    AdvancedFiltersService.availableFilters.next(structuredClone(filters));
    AdvancedFiltersService.availableFiltersUpdateSubject.next({
      data: structuredClone(filters),
      dataForQueryCount: this.getDataForQueryCount(),
      searchTerm,
      extendedSearch,
      reason: reason,
      _allCount: AdvancedFiltersService._allCount,
    });
    this.updateActiveFiltersList();
  }

  getAvailableFiltersValue(): AvailableFilters {
    return AdvancedFiltersService.availableFilters.getValue();
  }

  getActiveFiltersValue(): ActiveFilters {
    const allParams = this.getDataForQueryCount();
    const {searchTerm, extendedSearch} = this.getSearchValue();
    const activeFilters: ActiveFilters = {};
    Object.keys(allParams).forEach(filterType => {
      const elements = allParams[filterType] || [];
      if (elements.length) {
        const values = elements.map(filter => filter.id);
        if (values.length > 0) {
          activeFilters[this.getListParamKey(filterType)] = values;
        }
      }
    });

    this.applyEffectiveRangeToFilters(
      activeFilters,
      BlockTypeEnum.RANGE_PRICE_OF_CREATION,
      FilterTypeRanges.PRICE,
      {fromKey: 'priceFrom', toKey: 'priceTo', endAliasKey: 'priceEnd'},
    );
    this.applyEffectiveRangeToFilters(
      activeFilters,
      BlockTypeEnum.RANGE_YEAR_OF_CREATION,
      FilterTypeRanges.YEAR,
      {fromKey: 'yearFrom', toKey: 'yearTo', endAliasKey: 'yearEnd'},
    );
    if (searchTerm) {
      activeFilters['searchTerm'] = searchTerm;
    }
    if (extendedSearch) {
      activeFilters['extendedSearch'] = true;
    }

    return activeFilters;
  }

  private getListParamKey(filterType: string): string {
    return filterType.startsWith('custom')
      ? snakeCase(filterType)
      : filterType;
  }

  clearActiveFilters(): void {
    const allParams = AdvancedFiltersService.availableFilters.getValue();
    Object.keys(allParams).forEach(filterType => {
      if (Array.isArray(allParams[filterType]) && allParams[filterType].length > 0) {
        allParams[filterType].forEach(filter => filter.active = false);
      }
    });
    // "Wyczyść wyniki" powinno przywracać brak filtrów, więc zakresy wracają
    // do null (pełny baseline), nie do min-max.
    this.filterRangeService.filterStart[BlockTypeEnum.RANGE_PRICE_OF_CREATION] = null;
    this.filterRangeService.filterEnd[BlockTypeEnum.RANGE_PRICE_OF_CREATION] = null;
    this.filterRangeService.filterStart[BlockTypeEnum.RANGE_YEAR_OF_CREATION] = null;
    this.filterRangeService.filterEnd[BlockTypeEnum.RANGE_YEAR_OF_CREATION] = null;
    this.setFilters({
      filters: allParams,
      searchTerm: '',
      extendedSearch: false,
    }, UpdateAvailableFiltersReason.userAction);
  }

  clearFilters(): void {
    // Zakresy żyją poza availableFilters — bez wyzerowania przeciekłyby
    // do pierwszego requestu listy po resecie filtrów.
    const {filterStart, filterEnd} = this.filterRangeService;
    Object.keys(filterStart).forEach(key => filterStart[key] = null);
    Object.keys(filterEnd).forEach(key => filterEnd[key] = null);
    this.setFilters({
      filters: {},
      searchTerm: '',
      extendedSearch: false,
    }, UpdateAvailableFiltersReason.systemUpdate);
    this.updateActiveFiltersList();
  }
}
